import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './auth-context';
import { authenticateAdvisorAdmin } from '../helpers/jwtAuth';

const AuthCheckContext = createContext(null);
const EMPTY_AUTH = { isAdmin: false, isAdvisor: false, isManager: false, userId: null, loading: false };

/**
 * Returns the current advisor/admin auth state.
 * Must be used inside AuthCheckProvider — throws if used outside.
 * @returns {{ isAdmin: boolean, isAdvisor: boolean, isManager: boolean, userId: string|null, loading: boolean }}
 */
export function useAuthCheck() {
    const context = useContext(AuthCheckContext);
    if (!context) {
        throw new Error('useAuthCheck must be used within an AuthCheckProvider');
    }
    return context;
}

/**
 * Checks advisor/admin/manager role status on mount and whenever the access token changes.
 * Provides role flags via AuthCheckContext for permission-gated UI.
 */
export function AuthCheckProvider({ children }) {
    const { accessToken } = useAuth();
    const [checkedAuth, setCheckedAuth] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const authenticate = async () => {
            if (!accessToken) return;
            try {
                const auth = await authenticateAdvisorAdmin(accessToken);
                if (!cancelled) setCheckedAuth({ token: accessToken, value: {
                    isAdmin: auth.is_admin,
                    isAdvisor: auth.is_advisor,
                    isManager: auth.is_manager,
                    userId: auth.user_id,
                    loading: false
                } });
            } catch {
                if (!cancelled) setCheckedAuth({ token: accessToken, value: EMPTY_AUTH });
            }
        };
        authenticate();
        return () => { cancelled = true; };
    }, [accessToken]);

    // Never reuse a previous identity's roles while a different token is checked.
    const authState = !accessToken ? EMPTY_AUTH : checkedAuth?.token === accessToken
        ? checkedAuth.value : { ...EMPTY_AUTH, loading: true };

    return (
        <AuthCheckContext.Provider value={authState}>
            {children}
        </AuthCheckContext.Provider>
    );
}
